import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconLeft } from '../components/icons';
import { mobileMessage } from '../components/mobileFeedback';

type ReplyTarget = { id: string; author: string; content: string };
type Props = {
  replyTo: ReplyTarget | null;
  onClose: () => boolean;
  onSubmit: () => Promise<void>;
  content: string;
  onContentChange: (value: string) => void;
  busy: boolean;
};

function MobileTaskCommentSheet({ replyTo, onClose, onSubmit, content, onContentChange, busy }: Props) {
  const [closing, setClosing] = useState(false);
  const interactionRef = useRef<'closing' | 'submitting' | undefined>(undefined);
  const close = () => {
    if (busy || interactionRef.current) return;
    interactionRef.current = 'closing'; setClosing(true);
  };

  useLayoutEffect(() => {
    if (closing && busy) { interactionRef.current = undefined; setClosing(false); }
  }, [closing, busy]);

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') close(); };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [busy]);

  const submit = () => {
    if (busy || interactionRef.current) return;
    if (!content.trim()) { mobileMessage.warning('请输入评论内容'); return; }
    interactionRef.current = 'submitting';
    const release = () => { if (interactionRef.current === 'submitting') interactionRef.current = undefined; };
    void onSubmit().then(release, release);
  };

  return <div className={`mobile-action-layer${closing ? ' is-closing' : ''}`} role="dialog" aria-modal="true" aria-label={replyTo ? `回复${replyTo.author}` : '发表评论'}>
    <button className="mobile-action-layer__mask" type="button" aria-label="关闭评论" disabled={busy || closing} onClick={close} />
    <div className="mobile-action-page" onAnimationEnd={event => {
      if (event.target !== event.currentTarget || event.animationName !== 'mobile-action-slide-out' || interactionRef.current !== 'closing') return;
      if (busy || !onClose()) { interactionRef.current = undefined; setClosing(false); }
    }}>
      <header className="mobile-action-page__header"><button type="button" aria-label="返回评论" disabled={busy || closing} onClick={close}><IconLeft /></button><h2>{replyTo ? `回复 ${replyTo.author}` : '发表评论'}</h2></header>
      <div className="mobile-action-page__body"><section className="mobile-action-page__section">
        {replyTo ? <div className="mobile-comment-reply-context">回复 <strong>{replyTo.author}</strong> 的评论<p>{replyTo.content}</p></div> : null}
        <label className="mobile-action-page__label" htmlFor="mobile-comment-content">评论内容</label>
        <textarea id="mobile-comment-content" value={content} disabled={busy || closing} onChange={(event) => { if (!busy && !interactionRef.current) onContentChange(event.target.value); }} placeholder={replyTo ? `回复 ${replyTo.author}` : '请输入评论内容'} rows={6} />
      </section></div>
      <footer className="mobile-action-page__footer"><button type="button" disabled={busy || closing || !content.trim()} onClick={submit}>{replyTo ? '发表回复' : '发表评论'}</button></footer>
    </div>
  </div>;
}

export default MobileTaskCommentSheet;
